import { useState, useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import logo from "../../assets/images/logo.png";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();

  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  const renderRoutes = routes.map(({ type, name, icon, route, children, noCollapse, key }) => {
    let returnValue;

    if (type === "collapse") {
      const isOpen = openSections[name];

      returnValue = (
        <div key={key}>
          <NavLink
            to={route}
            rel="noreferrer"
            onClick={(e) => {
              if (children) {
                e.preventDefault(); // Prevents immediate navigation
                toggleSection(name); // Toggle submenu instead of closing
              } else {
                if (window.innerWidth < 1200) closeSidenav(); // Close only if no submenu
              }
            }}
          >
            <SidenavCollapse name={name} icon={icon} active={location.pathname === route} />
          </NavLink>

          {children && isOpen && (
            <div style={{ paddingLeft: "20px" }}>
              {children.map((child) => (
                <NavLink
                  key={child.key}
                  to={child.route}
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <SidenavCollapse
                    name={child.name}
                    icon={child.icon}
                    active={location.pathname === child.route}
                    noCollapse={noCollapse}
                  />
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color="textPrimary"
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {name}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          sx={{ borderColor: "#000000" }}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        {/* Close Sidenav button for small screens */}
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>

        {/* Logo and Brand Name Section */}
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          sx={{ textDecoration: "none", height: "auto" }} 
        >
          <MDBox
            component="img"
            src={logo}
            alt="Logo"
            width={miniSidenav ? "40px" : "70px"}
            height={miniSidenav ? "40px" : "70px"}
            sx={{
              marginBottom: miniSidenav ? "0px" : "8px",
              transition: "width 0.2s ease-in-out, height 0.2s ease-in-out, margin-bottom 0.2s ease-in-out",
            }}
          />
          {!miniSidenav && (
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              sx={{
                color: "#000000",
                opacity: miniSidenav ? 0 : 1,
                transition: "opacity 0.2s ease-in-out",
                
              }}
            >
              {brandName}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;